import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import { reasonOf } from '@/lib/api/errors';
import { candidateKeys } from '@/features/candidates/keys';
import { duplicateKeys } from '@/features/duplicates/api';
import { eventKeys } from '@/features/events/api';
import { ruleKeys } from '@/features/rules/keys';
import { auditKeys } from './keys';
import { AUDIT_PAGE_SIZE, toApiQuery, type AuditSearch } from './search';
import type { BackToSuggest } from './vocab';

export const auditQuery = (search: AuditSearch) =>
  queryOptions({
    queryKey: auditKeys.list(search),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/audit', { params: { query: toApiQuery(search) } })),
    placeholderData: keepPreviousData,
  });

/** Everything recorded, for saying how much a filter hides. */
export const auditCountQuery = () =>
  queryOptions({
    queryKey: auditKeys.count(),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/audit', { params: { query: { pageSize: 1 } } })).then(
        (page) => page.totalCount,
      ),
  });

export const auditPeopleQuery = () =>
  queryOptions({
    queryKey: auditKeys.people(),
    queryFn: () => unwrap(api.GET('/api/scraping/audit/people')),
    staleTime: 60_000,
  });

export { AUDIT_PAGE_SIZE };

export type { BackToSuggest };

/** Rules decided since a Belgrade day; a count over the trail's page. */
export const ruleDecisionCountQuery = (from: string) =>
  queryOptions({
    queryKey: auditKeys.ruleDecisions(from),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/audit', {
          params: { query: { actorKind: ['Rule'], from, pageSize: 1 } },
        }),
      ).then((page) => page.totalCount),
  });

export interface UndoOutcome {
  undone: string[];
  refused: { entryId: string; reason: string }[];
  /** Rules the undone decisions moved back to Suggest, each once. */
  backToSuggest: BackToSuggest[];
}

/**
 * Undoes entries newest first, trying the rest past a refusal. A plain function, not a mutation,
 * so a toast's Undo still hears back after the page that offered it has gone.
 */
export async function undoEntries(
  queryClient: QueryClient,
  entryIds: string[],
): Promise<UndoOutcome> {
  const outcome: UndoOutcome = { undone: [], refused: [], backToSuggest: [] };
  try {
    for (const entryId of [...entryIds].reverse()) {
      try {
        const { ruleBackToSuggest } = await unwrap(
          api.POST('/api/scraping/audit/{id}/undo', { params: { path: { id: entryId } } }),
        );
        outcome.undone.push(entryId);
        if (
          ruleBackToSuggest &&
          !outcome.backToSuggest.some((r) => r.ruleId === ruleBackToSuggest.ruleId)
        ) {
          outcome.backToSuggest.push(ruleBackToSuggest);
        }
      } catch (error) {
        outcome.refused.push({ entryId, reason: reasonOf(error) ?? 'The server gave no reason.' });
      }
    }
  } finally {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: auditKeys.all }),
      queryClient.invalidateQueries({ queryKey: candidateKeys.all }),
      queryClient.invalidateQueries({ queryKey: duplicateKeys.all }),
      queryClient.invalidateQueries({ queryKey: eventKeys.all }),
      queryClient.invalidateQueries({ queryKey: ruleKeys.all }),
    ]);
  }
  return outcome;
}

/** Undo from the trail itself, with its busy state. */
export function useUndoEntries() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: (entryIds: string[]) => undoEntries(queryClient, entryIds) });
}
